import { format, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SubscriptionDto } from '@/Domain/api/Subscription/dto/Subscription.dto';
import { SubscriptionPlansDto } from '@/Domain/api/Subscription/dto/SubscriptionPlans.dto';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import { CancelSubscriptionDialog } from '@/Domain/components/Subscription/CancelSubscription.dialog';
import { ChangePlanDialog } from '@/Domain/components/Subscription/ChangePlanDialog/ChangePlan.dialog';
import { MembershipCardAction } from '@/Domain/components/Subscription/MembershipCard/MembershipCardAction';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { SubscriptionHelper } from '@/Domain/helper/Subscription.helper';
import { useChangeSubscriptionStatus } from '@/Domain/hooks/Subscription/useChangeSubscriptionStatus.hook';
import { useDraftSubscription } from '@/Domain/hooks/Subscription/useDraftSubscription.hook';
import { formatCurrency } from '@/Domain/utils/formatCurrency';

type Props = {
  isBillingYearly: boolean;
  interval: SubscriptionIntervalEnum;
  currentPlan: SubscriptionDto['current_plan'];
  subscription: SubscriptionDto['subscription'];
  plans: SubscriptionPlansDto['products'];
};

export const MembershipCard = ({
  currentPlan,
  subscription,
  plans,
  interval,
  isBillingYearly,
}: Props) => {
  const { t } = useTranslation('subscription_page');
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isChangePlanDialogOpen, setIsChangePlanDialogOpen] = useState(false);

  const { handleChangeSubscriptionStatus } = useChangeSubscriptionStatus({
    cancelCallback: () => setIsCancelDialogOpen(false),
    activeCallback: () => setIsChangePlanDialogOpen(false),
  });

  const draftSubscription = useDraftSubscription();

  const priceId = useMemo(() => {
    const productByTier: Partial<Record<TierEnum, (typeof plans)[string]>> = {
      [TierEnum.STARTER]: plans.starter,
      [TierEnum.BASIC_2026]: plans.basic_2026,
      [TierEnum.ESSENTIAL_2026]: plans.essential_2026,
      [TierEnum.PROFESSIONAL_2026]: plans.professional_2026,
      [TierEnum.BUSINESS_2026]: plans.business_2026,
    };
    const product = productByTier[currentPlan.tier];
    if (!product) return undefined;
    return isBillingYearly ? product.price_annually : product.price_monthly;
  }, [currentPlan.tier, isBillingYearly, plans]);

  const handleOpenChangePlanDialog = () => {
    if (priceId) {
      draftSubscription.handleDraft(priceId);
      setIsChangePlanDialogOpen(true);
    }
  };

  const nextPayment = useMemo(() => {
    if (subscription.canceled) {
      return t('status.cancel');
    }

    return subscription.next_payment_on
      ? format(parseISO(subscription.next_payment_on), 'd MMMM, yyyy')
      : '-';
  }, [subscription.canceled, subscription.next_payment_on]);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('membership_card.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between text-sm">
            <div>
              <p className="text-foreground-subtle mb-1.5">{t('membership_card.plan_name')}</p>
              <span className="text-foreground font-medium">
                {SubscriptionHelper.getTrierLabel(currentPlan.tier, subscription.status)}
              </span>
            </div>
            <div>
              <p className="text-foreground-subtle mb-1.5">{t('membership_card.next_payment')}</p>
              <span className="text-foreground font-medium">{nextPayment}</span>
            </div>
            <div>
              <p className="text-foreground-subtle mb-1.5">{t('membership_card.billing_cycle')}</p>
              <span className="text-foreground font-medium">
                {subscription.interval
                  ? SubscriptionHelper.getIntervalLabel(subscription.interval)
                  : '-'}
              </span>
            </div>
            <div>
              <p className="text-foreground-subtle mb-1.5">{t('membership_card.total')}</p>
              <span className="text-foreground font-medium">
                {subscription.next_tier_payment
                  ? `${formatCurrency(subscription.next_tier_payment)}`
                  : '-'}
              </span>
            </div>
          </div>
        </CardContent>
        <CardFooter className="gap-4 mt-auto">
          <MembershipCardAction
            tier={currentPlan.tier}
            canceled={subscription.canceled}
            onCancelClick={() => setIsCancelDialogOpen(true)}
            onActivateClick={handleOpenChangePlanDialog}
          />
          {subscription.canceled && subscription.inactive_on && (
            <p className="text-sm text-foreground-subtle">
              <Trans
                i18nKey="subscription_page:membership_card.un_active_account_until"
                components={{
                  date: (
                    <span className="font-medium">
                      {format(new Date(subscription.inactive_on), 'dd MMM yyyy')}
                    </span>
                  ),
                }}
              />
            </p>
          )}
        </CardFooter>
      </Card>

      <CancelSubscriptionDialog
        isOpen={isCancelDialogOpen}
        onOpenChange={() => setIsCancelDialogOpen((prev) => !prev)}
        onCallback={(reason) => handleChangeSubscriptionStatus('cancel', reason)}
      />

      <ChangePlanDialog
        canceled={subscription.canceled}
        nextTier={currentPlan.tier}
        currentPlan={currentPlan}
        data={draftSubscription.data}
        isOpen={isChangePlanDialogOpen}
        isPending={draftSubscription.isPending}
        interval={interval}
        onOpenChange={() => {
          setIsChangePlanDialogOpen((prev) => !prev);
          draftSubscription.handleCancel();
        }}
        onCallback={() => handleChangeSubscriptionStatus('activate')}
      />
    </>
  );
};
